// CDiceScene — 原 APP GameOpenScene 擲骰開局過場（每一局 → dice → game）
// 對應反編譯原版 GameOpenScene + DiceAnimEntity + WindRoundAnim（見 mahjongking-ref skill）。
//
// 流程（與原版一致）：
// 1. 三顆骰子從外圍飛入骰盅，邊滾動殘影、X 緩出 + Y 彈跳落定，落定顯示隨機點數
// 2. 骰子落定後，骰盅上方每 0.4 秒直接顯示「圈風字 + 風 + 莊風字 + 局」
// 3. 第四字停留 0.9 秒 → 真實模式進牌局（CGameScene）；預覽模式停在原地，按 R 重播
//
// 進入方式：
// - 正式流程：首頁／續局 → scene.start(Dice, { autoStartGame: true }) → 播完進 Game
// - 預覽：網址 ?scene=Dice（boot 直接路由，無 autoStartGame）→ 播完不進牌局，按 R 重播整段
//
// === 使用圖片 ===
// - public/assets/dice/dice-dish.png（key: DICE_DISH_KEY）— 骰盅底盤
// - public/assets/dice/dice-faces.png（key: DICE_FACES_KEY，108×108 ×6）— 落定點數
// - public/assets/dice/dice-roll.png（key: DICE_ROLL_KEY，108×108 ×6）— 飛行中滾動殘影
// - public/assets/dice/round-words.png（key: ROUND_WORDS_KEY，144×144 ×6）— 東南西北風局字
// - 牌桌背景：createTableBackground（純 Graphics，見 table-background.ts）
// - 預覽提示框：CKeyboardHintBox（純 DOM <div>，左上角，見 keyboard-hint-box.ts）

import Phaser from 'phaser';
import { stopBackgroundMusic } from '@audio/scene-audio';

import { SCREEN_CENTER_X, SCREEN_CENTER_Y } from '@config/game-config';
import {
  RoundIndex,
  Seat,
  Wind,
  type IRoundPlan,
  type TDiceValues,
  type TSeat,
  type TWind,
} from '@core/index';
import { traceGameSet } from '../debug/round-trace';
import { loadGameSave, loadSettings } from '@storage/index';
import { PAGE_THEME } from '@ui/page-skin';
import { createTableBackground } from '@ui/table-background';
import { CKeyboardHintBox } from '@ui/keyboard-hint-box';

import {
  DICE_DISH_KEY,
  DICE_FACES_KEY,
  DICE_ROLL_KEY,
  ROUND_WORDS_KEY,
  RoundWordFrame,
  SFX_DICE_KEY,
  SFX_OPEN_WIND_KEY_BY_ROUND,
} from './boot-scene';
import { buildGameOpenTimeline } from './game-open-timing';
import {
  DICE_DISPLAY_SCALE,
  createRandomDiceLandingOffsets,
  diceLandingCenters,
} from './dice-layout';
import { SceneKey } from './scene-keys';

// ============================================================
// 版面常數
// ============================================================

const DISH_CX = SCREEN_CENTER_X; // 骰盅中心 X
const APP_DIP_SCALE = 1 / 2.25; // 原 APP MahjongUtils.dip2Px
const DISH_SIZE = 342; // 原始骰盅素材尺寸
const DICE_FRAME_SIZE = 108; // 骰子 frame 尺寸
const OPEN_SCENE_MARGIN_Y = 150 * APP_DIP_SCALE; // GameOpenScene marginHeight
const DISH_CY = SCREEN_CENTER_Y + OPEN_SCENE_MARGIN_Y; // 骰盅中心 Y（置中偏下）
const DISH_SCALE = 1; // 骰盅縮放

const DICE_SCALE = DICE_DISPLAY_SCALE; // 配合 342px 底盤，三顆骰子可完整落在內盤且互不重疊
const DICE_ROLL_FRAME_RATE = 10; // DiceEntity 每格 100ms
const DICE_HALF_SIZE = DICE_FRAME_SIZE / 2;
const DISH_LEFT = DISH_CX - DISH_SIZE / 2;
const DISH_TOP = DISH_CY - DISH_SIZE / 2;

// 起點沿用原 APP DiceAnimEntity 的三方飛入方向；落點在每次播放時重新隨機產生。
const DICE_START_POSITIONS: ReadonlyArray<{
  startX: number;
  startY: number;
}> = [
  {
    startX: DISH_LEFT - 600 * APP_DIP_SCALE + DICE_HALF_SIZE,
    startY: DISH_TOP - 270 * APP_DIP_SCALE + DICE_HALF_SIZE,
  },
  {
    startX: DISH_LEFT - 620 * APP_DIP_SCALE + DICE_HALF_SIZE,
    startY: DISH_TOP - 150 * APP_DIP_SCALE + DICE_HALF_SIZE,
  },
  {
    startX: DISH_LEFT + 700 * APP_DIP_SCALE + DICE_HALF_SIZE,
    startY: DISH_TOP - 200 * APP_DIP_SCALE + DICE_HALF_SIZE,
  },
];

const WORD_FRAME_SIZE = 144; // round-words 單格尺寸
const WORDS_Y = 155; // 骰盅上方；與階段標題、對家方位字各自保留間距
const WORD_SCALE = 1; // 原 APP 使用素材原尺寸
const WORD_STEP_X = WORD_FRAME_SIZE; // 四字相鄰排列
const GAME_OPEN_TIMELINE = buildGameOpenTimeline();
const PHASE_TITLE_Y = 55;
const PHASE_TITLE_FONT_SIZE = 32;
const BETWEEN_PHASE_DELAY_MS = 800;
const SKIP_RESULT_HOLD_MS = 250;
const SEAT_LABEL_FONT_SIZE = 27;
const SEAT_LABEL_NORMAL_COLOR = '#9cc9a5';
const SEAT_LABEL_ACTIVE_COLOR = '#ffe16a';
const SEAT_LABEL_STROKE_COLOR = '#5b2300';
const SEAT_LABEL_STROKE_WIDTH = 5;
const SEAT_LABEL_ACTIVE_SCALE = 1.18;

const SEAT_LABEL_LAYOUT: Record<TSeat, { readonly x: number; readonly y: number }> = {
  [Seat.Self]: { x: 640, y: 655 }, // 本家：骰盅下方
  [Seat.Right]: { x: 895, y: 430 }, // 下家：骰盅右方
  [Seat.Opposite]: { x: 640, y: 245 }, // 對家：骰盅上緣
  [Seat.Left]: { x: 385, y: 430 }, // 上家：骰盅左方
};

// ============================================================
// 風 → round-words frame 對應
// ============================================================

const WIND_TO_ROUND_FRAME: Record<TWind, number> = {
  [Wind.East]: RoundWordFrame.East,
  [Wind.South]: RoundWordFrame.South,
  [Wind.West]: RoundWordFrame.West,
  [Wind.North]: RoundWordFrame.North,
};

const ROUND_INDEX_TO_WIND: Record<number, TWind> = {
  [RoundIndex.East]: Wind.East,
  [RoundIndex.South]: Wind.South,
  [RoundIndex.West]: Wind.West,
  [RoundIndex.North]: Wind.North,
};

const SEAT_NAME: Record<TSeat, string> = {
  [Seat.Self]: '本家',
  [Seat.Right]: '下家',
  [Seat.Opposite]: '對家',
  [Seat.Left]: '上家',
};

const DICE_ROLL_ANIM = 'dice-roll-spin';

export interface IDiceSceneData {
  readonly autoStartGame?: boolean;
}

interface IDicePresentationPhase {
  readonly label: string;
  readonly resultLabel: string;
  readonly resultSeat: TSeat;
  readonly dice: TDiceValues;
  readonly showWords: boolean;
}

export class CDiceScene extends Phaser.Scene {
  private preview = false;
  private dice: Phaser.GameObjects.Sprite[] = [];
  private words: Phaser.GameObjects.Sprite[] = [];
  private seatLabels = new Map<TSeat, Phaser.GameObjects.Text>();
  private hint?: CKeyboardHintBox;
  private replaying = false;
  private roundPlan!: IRoundPlan;
  private phaseTitle!: Phaser.GameObjects.Text;
  private phases: IDicePresentationPhase[] = [];
  private phaseIndex = 0;
  private skipping = false;

  constructor() {
    super({ key: SceneKey.Dice });
  }

  create(data: IDiceSceneData): void {
    stopBackgroundMusic(this);
    // 預覽 = 非正式每局流程（網址 ?scene=Dice 直接路由時無 autoStartGame）
    this.preview = data?.autoStartGame !== true;
    this.roundPlan = this.resolveRoundPlan();
    this.phases = this.buildPhases();
    this.phaseIndex = 0;
    if (!this.preview) {
      const save = loadGameSave();
      const session = save.activeSession;
      if (this.roundPlan.dealerDice) {
        traceGameSet('dice.dealer.rolled', session, {
          dice: this.roundPlan.dealerDice,
        });
        traceGameSet('dealer.selected', session, {
          dealerSeat: this.roundPlan.dealerSeat,
        });
      }
      traceGameSet('dice.opening.rolled', session, {
        dice: this.roundPlan.openingDice,
      });
      traceGameSet('wall.opened', session, {
        openingSeat: this.roundPlan.openingSeat,
        openingStackIndex: this.roundPlan.openingStackIndex,
        wallStartStackIndex: this.roundPlan.wallStartStackIndex,
      });
    }

    createTableBackground(this);
    this.phaseTitle = this.add
      .text(DISH_CX, PHASE_TITLE_Y, '', {
        color: PAGE_THEME.text,
        fontFamily: PAGE_THEME.font,
        fontSize: `${PHASE_TITLE_FONT_SIZE}px`,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(3);

    // 骰盅
    this.add.image(DISH_CX, DISH_CY, DICE_DISH_KEY).setOrigin(0.5).setScale(DISH_SCALE);
    this.createSeatLabels();

    // 滾動動畫（共用一份）
    if (!this.anims.exists(DICE_ROLL_ANIM)) {
      this.anims.create({
        key: DICE_ROLL_ANIM,
        frames: this.anims.generateFrameNumbers(DICE_ROLL_KEY, { start: 0, end: 5 }),
        frameRate: DICE_ROLL_FRAME_RATE,
        repeat: -1,
      });
    }

    // 三顆骰子（一顆一個 Sprite：飛行時播 roll 動畫、落定切成 faces 隨機點數）
    this.dice = DICE_START_POSITIONS.map(() =>
      this.add
        .sprite(DISH_CX, DISH_CY, DICE_ROLL_KEY, 0)
        .setOrigin(0.5)
        .setScale(DICE_SCALE)
        .setDepth(1)
        .setVisible(false)
    );

    // 風局四字：[圈風字, 風, 莊風字, 局]，初始隱藏
    const frames = [
      WIND_TO_ROUND_FRAME[this.roundPlan.prevailingWind],
      RoundWordFrame.Wind,
      WIND_TO_ROUND_FRAME[ROUND_INDEX_TO_WIND[this.roundPlan.roundIndex]!],
      RoundWordFrame.Round,
    ];
    const startX = DISH_CX - (WORD_STEP_X * (frames.length - 1)) / 2;
    this.words = frames.map((frame, i) =>
      this.add
        .sprite(startX + i * WORD_STEP_X, WORDS_Y, ROUND_WORDS_KEY, frame)
        .setOrigin(0.5)
        .setScale(WORD_SCALE)
        .setDepth(2)
        .setVisible(false)
    );

    if (this.preview) {
      this.hint = new CKeyboardHintBox(this);
      this.hint.setLines(['【預覽模式 擲骰開局】', 'R：重播整段動畫', 'Enter／點擊：跳過']);
      this.input.keyboard?.on('keydown-R', () => this.replay());
    }
    this.input.keyboard?.on('keydown-ENTER', () => this.skipSequence());
    this.input.keyboard?.on('keydown-ESC', () => this.scene.start(SceneKey.Home));
    this.input.on('pointerdown', () => this.skipSequence());

    this.playSequence();
  }

  private resolveRoundPlan(): IRoundPlan {
    if (!this.preview) {
      const plan = loadGameSave().activeSession?.currentRound;
      if (!plan) throw new Error('DiceScene requires a saved round plan');
      return plan;
    }
    const openingOnly = new URLSearchParams(window.location.search).get('phase') === 'opening';
    return {
      roundId: 'preview-round',
      transactionId: 'preview-transaction',
      dealerSeat: Seat.Right,
      dealerStreak: 0,
      prevailingWind: Wind.East,
      roundIndex: RoundIndex.East,
      openingSeat: Seat.Left,
      openingStackIndex: 9,
      wallStartStackIndex: 63,
      seatWinds: [Wind.South, Wind.West, Wind.North, Wind.East],
      dealerDice: openingOnly ? null : [1, 1, 2],
      openingDice: [2, 3, 4],
      wallSeed: 2026,
      base: 100,
      taiUnit: 20,
      settlementApplied: false,
    };
  }

  private buildPhases(): IDicePresentationPhase[] {
    const phases: IDicePresentationPhase[] = [];
    if (this.roundPlan.dealerDice) {
      phases.push({
        label: '起莊｜本家擲骰',
        resultLabel: `起莊結果｜${SEAT_NAME[this.roundPlan.dealerSeat]}坐莊`,
        resultSeat: this.roundPlan.dealerSeat,
        dice: this.roundPlan.dealerDice,
        showWords: false,
      });
    }
    phases.push({
      label: `開門｜${SEAT_NAME[this.roundPlan.dealerSeat]}擲骰`,
      resultLabel: `開門結果｜${SEAT_NAME[this.roundPlan.openingSeat]}為東風`,
      resultSeat: this.roundPlan.openingSeat,
      dice: this.roundPlan.openingDice,
      showWords: true,
    });
    return phases;
  }

  /** 播放音效（遵守設定的音效開關）。 */
  private playSfx(key: string): void {
    if (!loadSettings().audioOn) return;
    this.sound.play(key);
  }

  /** 播放整段原 APP 開局動畫：擲骰 → 逐字風局 → 進牌局 / 可重播。 */
  private playSequence(): void {
    this.replaying = true;
    this.skipping = false;
    const phase = this.phases[this.phaseIndex]!;
    this.phaseTitle.setText(phase.label);
    this.highlightSeat(null);
    this.words.forEach((w) => w.setVisible(false).setScale(WORD_SCALE));

    // 骰子搖落聲開場即響；風局語音在骰子動畫進度 90%（1.8 秒）時響。
    this.playSfx(SFX_DICE_KEY);
    if (phase.showWords) {
      this.time.delayedCall(GAME_OPEN_TIMELINE.openWindSoundAtMs, () =>
        this.playSfx(
          SFX_OPEN_WIND_KEY_BY_ROUND[this.roundPlan.prevailingWind][
            ROUND_INDEX_TO_WIND[this.roundPlan.roundIndex]!
          ]
        )
      );
    }

    const landingCenters = diceLandingCenters(
      { x: DISH_CX, y: DISH_CY },
      createRandomDiceLandingOffsets()
    );
    this.dice.forEach((die, i) => {
      const start = DICE_START_POSITIONS[i]!;
      const landing = landingCenters[i]!;
      die
        .setVisible(true)
        .setTexture(DICE_ROLL_KEY, 0)
        .setPosition(start.startX, start.startY)
        .play(DICE_ROLL_ANIM);
      this.tweens.add({
        targets: die,
        props: {
          x: { value: landing.x, ease: 'Cubic.easeOut' },
          y: { value: landing.y, ease: 'Bounce.easeOut' },
        },
        duration: GAME_OPEN_TIMELINE.diceFlyMs,
        onComplete: () => {
          die.stop();
          // 落定：顯示 round plan 事先保存的既定點數，不在動畫結束後重新隨機。
          die.setTexture(DICE_FACES_KEY, phase.dice[i]! - 1);
        },
      });
    });

    // 原 APP 只逐字切換 visible，沒有縮放／彈出 tween。
    if (phase.showWords) {
      this.words.forEach((word, i) => {
        this.time.delayedCall(GAME_OPEN_TIMELINE.wordRevealAtMs[i]!, () => word.setVisible(true));
      });
    }

    this.time.delayedCall(GAME_OPEN_TIMELINE.finishAtMs, () => {
      this.phaseTitle.setText(phase.resultLabel);
      this.highlightSeat(phase.resultSeat);
      if (this.phaseIndex < this.phases.length - 1) {
        this.phaseIndex += 1;
        this.time.delayedCall(BETWEEN_PHASE_DELAY_MS, () => this.playSequence());
        return;
      }
      this.onSequenceDone();
    });
  }

  private onSequenceDone(): void {
    this.replaying = false;
    this.skipping = false;
    if (!this.preview) {
      this.scene.start(SceneKey.Game);
    }
  }

  /** 重播整段動畫（預覽模式 R 鍵）；播放中按則忽略。 */
  private replay(): void {
    if (this.replaying) return;
    this.phaseIndex = 0;
    this.playSequence();
  }

  /**
   * 跳過只省略演出：骰值、莊家、開門家與 round plan 一律沿用進場前已保存的資料。
   * 先停止目前所有骰子 tween／timer，再顯示最後一擲的既定結果，避免舊 callback
   * 在 GameScene 啟動後重入流程。
   */
  private skipSequence(): void {
    if (!this.replaying || this.skipping) return;
    this.skipping = true;
    this.tweens.killAll();
    this.time.removeAllEvents();

    this.phaseIndex = this.phases.length - 1;
    const phase = this.phases[this.phaseIndex]!;
    const landingCenters = diceLandingCenters(
      { x: DISH_CX, y: DISH_CY },
      createRandomDiceLandingOffsets()
    );
    this.dice.forEach((die, index) => {
      die
        .stop()
        .setVisible(true)
        .setPosition(landingCenters[index]!.x, landingCenters[index]!.y)
        .setTexture(DICE_FACES_KEY, phase.dice[index]! - 1);
    });
    this.phaseTitle.setText(phase.resultLabel);
    this.highlightSeat(phase.resultSeat);
    this.words.forEach((word) => word.setVisible(phase.showWords));
    this.replaying = false;

    if (this.preview) {
      this.skipping = false;
      return;
    }
    this.time.delayedCall(SKIP_RESULT_HOLD_MS, () => this.onSequenceDone());
  }

  private createSeatLabels(): void {
    for (const seat of [Seat.Self, Seat.Right, Seat.Opposite, Seat.Left] as const) {
      const spot = SEAT_LABEL_LAYOUT[seat];
      const label = this.add
        .text(spot.x, spot.y, SEAT_NAME[seat], {
          color: SEAT_LABEL_NORMAL_COLOR,
          fontFamily: PAGE_THEME.font,
          fontSize: `${SEAT_LABEL_FONT_SIZE}px`,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(3);
      this.seatLabels.set(seat, label);
    }
  }

  private highlightSeat(activeSeat: TSeat | null): void {
    for (const [seat, label] of this.seatLabels) {
      const active = seat === activeSeat;
      label
        .setColor(active ? SEAT_LABEL_ACTIVE_COLOR : SEAT_LABEL_NORMAL_COLOR)
        .setScale(active ? SEAT_LABEL_ACTIVE_SCALE : 1)
        .setStroke(
          active ? SEAT_LABEL_STROKE_COLOR : '#000000',
          active ? SEAT_LABEL_STROKE_WIDTH : 0
        );
    }
  }
}
